<template>
  <div>
    <v-app-bar
      absolute
      app
      dark
      flat
      prominent
    >
      <!-- mobile menu open button and logo -->
      <v-container class="py-0 fill-height hidden-sm-and-up">
        <v-row align="center" class="fill-height" no-gutters>
          <v-col cols="auto">
            <v-btn
              v-if="ready"
              :ripple="false"
              fab
              small
              text
              @click.stop="drawer = !drawer"
            >
              <v-icon v-text="icons.menu" />
            </v-btn>
          </v-col>
          <v-spacer />
          <!-- logo -->
          <v-col cols="auto" class="d-flex">
            <v-toolbar-title class="mr-4 align-self-center d-flex align-center">
              <a :href="githubApp.url" :title="githubApp.title" class="mr-6">
                <v-img src="../assets/github-white-logo-sm.svg" width="32" />
              </a>
              <a :href="homepageUrl">
                <v-img src="../assets/algo_logo.svg" width="120" />
              </a>
            </v-toolbar-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- tablet and desktop nav -->
      <v-container class="py-0 fill-height hidden-xs-only">
        <v-row align="stretch" class="fill-height flex-nowrap" no-gutters>
          <!-- logo -->
          <v-col cols="auto" class="d-flex">
            <v-toolbar-title class="mr-4 align-self-center d-flex align-center">
              <a :href="homepageUrl">
                <v-img src="../assets/algo_logo.svg" width="120" />
              </a>
              <a :href="githubApp.url" :title="githubApp.title" class="ml-4">
                <v-img src="../assets/github-white-logo-sm.svg" width="32" />
              </a>
            </v-toolbar-title>
          </v-col>

          <!-- nav buttons -->
          <v-col class="d-flex hidden-sm-and-down">
            <v-btn
              v-if="ready"
              :ripple="false"
              :to="{name: 'projects'}"
              active-class="nav-button-active"
              class="ml-2 ml-md-5 nav-button align-self-stretch"
              text
            >
              {{ $t('Projects') }}
            </v-btn>
            <v-btn
              v-if="ready"
              :ripple="false"
              :to="{name: 'deposits', params: {projectId: 'all', sectionId: 'active'}}"
              :class="{'nav-button-active': $route.name && $route.name.startsWith('deposit')}"
              class="nav-button"
              text
            >
              {{ $t('Deposits') }}
            </v-btn>
            <template v-if="showAddDepositButton">
              <v-btn
                :ripple="false"
                :to="{name: 'new-deposit-select-type'}"
                class="ml-5 align-self-center hidden-sm-and-down"
                color="primary"
                rounded
              >
                {{ $t('Add new deposit') }}
              </v-btn>
              <v-btn
                :ripple="false"
                :title="$t('Add new deposit')"
                :to="{name: 'new-deposit-select-type'}"
                class="ml-5 align-self-center hidden-md-and-up"
                color="primary"
                fab
              >
                <v-icon v-text="icons.plus" />
              </v-btn>
            </template>
          </v-col>

          <v-spacer class="hidden-xs-only" />

          <!-- user and plan -->
          <v-col class="d-md-flex px-2 px-md-5 hidden-sm-and-down" cols="auto">
            <div v-if="ready" class="profile-info align-self-center body-2">
              <div class="profile-name mb-2">
                {{ profile.name }}
              </div>
              <div v-if="profileLoaded && isBusinessAccount" class="profile-organization">
                <small>{{ organizationAndRole }}</small>
              </div>
              <div v-if="profileLoaded && !isBusinessAccount" class="text-no-wrap">
                <span class="hidden-md-and-down">{{ $t('Current plan') }}: </span>
                <router-link :to="{name: 'profile-subscription'}" class="text-capitalize">
                  {{ activePlan.name || $t('no active subscription') }}
                </router-link>
              </div>
            </div>
          </v-col>

          <!-- avatar and menu -->
          <v-col class="d-flex pl-2 pl-md-5 hidden-sm-and-down" cols="auto">
            <v-menu
              v-if="ready"
              bottom
              close-on-click
              content-class="profile-menu"
              nudge-bottom="90"
              open-delay="300"
              open-on-hover
            >
              <template v-slot:activator="{on}">
                <v-btn
                  :ripple="false"
                  class="nav-avatar-button align-self-center pr-0"
                  text
                  v-on="on"
                >
                  <app-avatar :image="profile.avatar" size="46" />
                  <v-icon right>
                    {{ icons.menuDown }}
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  :ripple="false"
                  class="hidden-md-and-up"
                  disabled
                >
                  {{ profile.name }}
                </v-list-item>
                <v-list-item
                  :ripple="false"
                  :to="{name: 'profile'}"
                  class="text-decoration-none"
                >
                  {{ $t('Profile') }}
                </v-list-item>
                <v-list-item :ripple="false" @click="doLogout">
                  {{ $t('Logout') }}
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <!-- mobile nav drawer -->
    <v-navigation-drawer
      v-if="ready"
      :value="drawer"
      mobile-breakpoint="xs"
      app
      temporary
      clipped
      @input="drawerInput"
    >
      <v-list>
        <v-list-item
          v-if="profileLoaded"
          :ripple="false"
          :to="{name: 'profile'}"
          class="text-decoration-none"
        >
          <app-avatar :image="profile.avatar" class="mr-2" size="32" />
          <div>
            {{ profile.name }}
            <template v-if="isBusinessAccount">
              <br>
              <small>{{ organizationAndRole }}</small>
            </template>
          </div>
        </v-list-item>

        <v-divider class="mb-4" />

        <v-list-item class="mb-4">
          <v-btn
            :disabled="$route.name === 'new-deposit-select-type'"
            :ripple="false"
            :to="{name: 'new-deposit-select-type'}"
            class="white--text"
            color="primary"
            elevation="0"
            block
            rounded
          >
            {{ $t('Add new deposit') }}
          </v-btn>
        </v-list-item>

        <v-list-item class="px-0">
          <v-tabs :hide-slider="notInVerticalTabsRoutes" optional vertical>
            <v-tab
              :ripple="false"
              :to="{name: 'projects'}"
              class="justify-start text-decoration-none"
            >
              {{ $t('Projects') }}
            </v-tab>
            <v-tab
              :ripple="false"
              :to="{name: 'deposits', params: {projectId: 'all', sectionId: 'active'}}"
              class="justify-start text-decoration-none"
            >
              {{ $t('Deposits') }}
            </v-tab>
            <v-tab
              :ripple="false"
              :to="{name: 'profile'}"
              class="justify-start text-decoration-none"
            >
              {{ $t('Profile') }}
            </v-tab>
          </v-tabs>
        </v-list-item>

        <v-list-item :ripple="false" class="mt-4" @click="doLogout">
          <v-icon left v-text="icons.logout" />
          {{ $t('Logout') }}
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import {mdiLogout, mdiMenu, mdiMenuDown, mdiPlus} from '@mdi/js'
import AppAvatar from '@/components/AppAvatar'

export default {
  name: 'AppNavUser',
  components: {AppAvatar},
  data () {
    return {
      homepageUrl: process.env.VUE_APP_SUBDOMAIN_LANDING,
      loggingOut: false,
      drawer: false,
      icons: {
        logout: mdiLogout,
        menu: mdiMenu,
        menuDown: mdiMenuDown,
        plus: mdiPlus
      }
    }
  },
  computed: {
    ...mapState({
      ready: state => state.ready,
      profileLoaded: state => state.loaders.profile.status,
      profile: state => state.profile,
      isBusinessAccount: state => state.profile.accountType === 'business',
      currentPage: state => state.deposits.pagination.currentPage,
      githubApp: state => state.config.app?.github || {},
    }),
    ...mapGetters({
      activePlan: 'getActivePlan'
    }),
    showAddDepositButton () {
      if (!(this.ready && this.profileLoaded)) {
        return false
      }
      if (
        this.$route.name === 'new-deposit' ||
        this.$route.name === 'new-deposit-select-type'
      ) {
        return false
      }
      if (this.isBusinessAccount && this.profile.role !== 'teammate') {
        return false
      }
      return true
    },
    notInVerticalTabsRoutes () {
      if (!this.$route.name) return true
      return !this.$route.name.startsWith('deposit') &&
        !this.$route.name.startsWith('project') &&
        !this.$route.name.startsWith('profile')
    },
    organizationAndRole () {
      if (!this.isBusinessAccount) return ''
      return this.profile.role === 'manager'
        ? `${this.profile.organization.name}, ${this.profile.role_title}`
        : this.profile.organization.name
    },
  },
  methods: {
    ...mapActions(['logout']),
    async doLogout () {
      this.loggingOut = true
      await this.$auth.logout()
      this.loggingOut = false
    },
    drawerInput (open) {
      this.drawer = open
    }
  }
}
</script>

<style lang="scss" scoped>
  header .v-toolbar__content {
    .nav-button {
      padding-left: 25px !important;
      padding-right: 25px !important;
      opacity: 0.42;
      height: 100%;

      &::before, &:hover::before {
        opacity: 0;
        background-color: transparent;
        border-bottom: 4px solid var(--v-primary-base);
      }

      &.nav-button-active {
        opacity: 1;

        &::before, &:hover::before {
          opacity: 1;
        }
      }
    }

    .nav-avatar-button {
      height: 100%;

      &::before, &:hover::before {
        opacity: 0;
        background-color: transparent;
      }
    }

    .profile-info {
      max-width: 15ch;
      max-height: 7rem;
      overflow: hidden;
      @media #{map-get($display-breakpoints, 'lg-and-up')} {
        max-width: 50ch;
      }
    }

    .profile-name {
      word-break: break-word;
    }

    .profile-organization {
      line-height: 1;
    }
  }

  .profile-menu {
    box-shadow: none;
    border: thin solid var(--v-outline-base);

    .v-list-item {
      &:not(:last-child) {
        border-bottom: thin solid var(--v-outline-base);
      }

      &:hover {
        background-color: var(--v-surface-darken1);

        &::before {
          opacity: 0;
        }
      }
    }
  }
</style>
